import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import axios, { AxiosResponse } from 'axios'
import { Artifact } from '@/types/Artifact'
import { useInstanceStore } from '@/store/backendInstances'
import { DocumentQueryPayload, DocumentQuerySelect } from '@/types/Query'
import backendHandler from '@/BackendAPI'
import { useLogsStore } from '@/store/logs'
import { useDownloadFile } from '@/composables/downloadFile'

function isResizableArtifact(artifactType: string): boolean {
  const resizableTypes = ['singleFrame', 'heatmap', 'longTimeExposure']
  return resizableTypes.includes(artifactType)
}

type ArtifactStoreRecord = {
  data?: Artifact
  payload: string | null
  resizable: boolean
  currentHeight: number | null
  downloaded: boolean
}

type DownloadPromiseHolder = {
  promise: Promise<string | null>
  height?: number
  controller: AbortController
}

interface ArtifactsState {
  downloadPromises: Map<string, Map<string, DownloadPromiseHolder>> //Dimension: GUID, ArtifactId, Promise and Height of current download (if applicable)
  artifacts: Record<string, Record<string, ArtifactStoreRecord>> //Dimension: GUID, ArtifactId, ArtifactStoreRecord
}

export const useArtifactStore = defineStore('artifacts', {
  state: (): ArtifactsState => ({
    downloadPromises: new Map(),
    artifacts: {},
  }),
  persist: false,
  share: { enable: false },
  getters: {
    isPayloadDownloading:
      state =>
      (artifactId: string, globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): boolean => {
        if (!state.downloadPromises.has(globalUniqueIdentifier)) return false
        if (!state.downloadPromises.get(globalUniqueIdentifier)?.has(artifactId)) return false
        return true
      },
    isPayloadDownloaded:
      state =>
      (artifactId: string, globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): boolean => {
        const artifactRecord = state.artifacts?.[globalUniqueIdentifier]?.[artifactId]
        return artifactRecord?.downloaded || false
      },
    getArtifact:
      state =>
      (artifactId: string, globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): Artifact | null => {
        const artifactRecord = state.artifacts?.[globalUniqueIdentifier]?.[artifactId]
        return artifactRecord?.data ? artifactRecord.data : null
      },
    getArtifactPayload:
      state =>
      (artifactId: string, globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): string | null => {
        const artifactRecord = state.artifacts?.[globalUniqueIdentifier]?.[artifactId]
        if (!artifactRecord) return null
        if (!artifactRecord.downloaded || !artifactRecord.payload) return null
        return artifactRecord.payload as string
      },
  },

  actions: {
    /**
     * Writes Artifact to 'this.artifacts' cache
     * @param artifact
     * @param globalUniqueIdentifier
     */
    cacheArtifact(artifact: Artifact, globalUniqueIdentifier: string) {
      if (!this.artifacts[globalUniqueIdentifier]) this.artifacts[globalUniqueIdentifier] = {}
      if (!this.artifacts[globalUniqueIdentifier][artifact.artifactId]) {
        this.artifacts[globalUniqueIdentifier][artifact.artifactId] = {
          data: artifact,
          downloaded: false,
          resizable: isResizableArtifact(artifact.type),
          payload: null,
          currentHeight: null,
        }
      } else {
        this.artifacts[globalUniqueIdentifier][artifact.artifactId].data = artifact
      }
    },

    /**
     * Standard Query Route for Artifacts, Caches Loaded Artifacts
     * @param {DocumentQueryPayload | null} queryPayload - If Null, no QueryPayload will be applied
     * @param {string} [globalUniqueIdentifier]
     * @return {Promise<Artifact[] | null | number>} - Returns Array of Artifacts, Null if Error, or Number if Count Projection
     */
    async queryArtifacts(
      queryPayload: DocumentQueryPayload | null,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<Artifact[] | null | number> {
      //@ts-expect-error backendHandler isn't typed yet
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/artifact/data/query',
        data: queryPayload ? queryPayload : undefined,
      })
      if (!response.ok) return null
      if (queryPayload?.projection === 'count') return response.data as number
      const artifacts = response.data as Artifact[]
      if (!Object.keys(queryPayload?.projection || {}).includes('flatten') && !Object.keys(queryPayload?.projection || {}).includes('only')) {
        artifacts.forEach(artifact => {
          this.cacheArtifact(artifact, globalUniqueIdentifier)
        })
      }
      return artifacts
    },

    /**
     * Standard Query One Route for Artifacts, Caches Loaded Artifact
     * @param {DocumentQueryPayload | null} queryPayload - If Null, no QueryPayload will be applied
     * @param {string} [globalUniqueIdentifier]
     * @return {Promise<Artifact | null | number>} - Returns Artifact, Null if Error OR If no Artifact was found, or Number if Count Projection
     * */
    async queryOneArtifact(
      queryPayload: DocumentQueryPayload | null,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<Artifact | null | number> {
      //@ts-expect-error backendHandler isn't typed yet
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/artifact/data/queryOne',
        data: queryPayload ? queryPayload : undefined,
      })
      if (!response.ok) return null
      if (queryPayload?.projection === 'count') return response.data as number
      const artifact = response.data as Artifact
      if (!Object.keys(queryPayload?.projection || {}).includes('flatten') && !Object.keys(queryPayload?.projection || {}).includes('only')) {
        this.cacheArtifact(artifact, globalUniqueIdentifier)
      }
      return artifact
    },

    /**
     * Fetches the Artifact Data for an Artifact. If skipCache is False, it tries to look up the artifact in the local cache first.
     * Otherwise (or if it wasn't found in the cache), it queries the backend for the artifact data, and caches it before returning.
     * @param {string} artifactId - DatabaseId of Artifact
     * @param {boolean} [skipCache=false] - Whether to skip looking up the artifact in the local cache
     * @param {string} [globalUniqueIdentifier] - Optional Global Unique Identifier for the instance to query. If not provided, uses the currently selected instance in the Instance Store
     * @return {Promise<Artifact | null>} - Returns the Artifact with the specified ID, or Null if it couldn't be found or an error occurred
     */
    async loadArtifactData(
      artifactId: string,
      skipCache: boolean = false,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<Artifact | null> {
      if (!skipCache) {
        const maybeCachedArtifact = this.getArtifact(artifactId, globalUniqueIdentifier)
        if (maybeCachedArtifact) return maybeCachedArtifact
      }
      return (await this.queryOneArtifact({ query: artifactId }, globalUniqueIdentifier)) as Artifact | null
    },

    /**
     * Downloads Artifact Payload.
     * If height is set to null, then the full size artifact will be loaded, and if there's already an artifact payload for this artifact with a size present it will be overwritten
     * @param artifactId
     * @param signal
     * @param height
     * @param higherPriority
     * @param globalUniqueIdentifier
     */
    async loadArtifactPayload(
      artifactId: string,
      signal?: AbortSignal,
      height: number | null = 1080,
      higherPriority: boolean = false,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<string | null> {
      const existingRecord = this.artifacts[globalUniqueIdentifier]?.[artifactId]

      if (existingRecord?.downloaded && existingRecord.payload) {
        const isSufficient =
          height === null ? existingRecord.currentHeight === null : existingRecord.currentHeight === null || existingRecord.currentHeight >= height

        if (isSufficient) return existingRecord.payload
      }

      const currentDownload = this.downloadPromises.get(globalUniqueIdentifier)?.get(artifactId)
      if (currentDownload) {
        const originalIsDownloading = currentDownload.height === undefined
        const isSufficientlyHigh = height !== null && currentDownload.height !== undefined && currentDownload.height >= height

        if (originalIsDownloading || isSufficientlyHigh) {
          return currentDownload.promise
        }

        try {
          await currentDownload.promise
          return this.loadArtifactPayload(artifactId, signal, height, higherPriority, globalUniqueIdentifier)
        } catch {}
      }

      if (signal?.aborted) return null
      const controller = new AbortController()
      signal?.addEventListener('abort', () => controller.abort())

      const promise = new Promise<string | null>(resolve => {
        backendHandler
          //@ts-expect-error backendHandler isn't typed yet
          .get(globalUniqueIdentifier, {
            path: '/api/artifact/' + artifactId,
            responseType: 'blob',
            params: { resizeY: height },
            signal: controller.signal,
            priority: higherPriority ? 4 : undefined,
          })
          .then((response: AxiosResponse & { ok: boolean }) => {
            if (!response.ok) {
              this.downloadPromises.get(globalUniqueIdentifier)?.delete(artifactId)
              return resolve(null)
            }

            if (!this.artifacts[globalUniqueIdentifier]) this.artifacts[globalUniqueIdentifier] = {}
            const existing = this.artifacts[globalUniqueIdentifier][artifactId] || {}

            const shouldUpdate = !existing.payload || height === null || (existing.currentHeight !== null && height > existing.currentHeight)

            if (shouldUpdate) {
              if (existing.payload) URL.revokeObjectURL(existing.payload)
              const blobUrl = URL.createObjectURL(response.data)
              this.artifacts[globalUniqueIdentifier][artifactId] = {
                ...existing,
                payload: blobUrl,
                currentHeight: height,
                downloaded: true,
                resizable: height !== null,
              }
              resolve(blobUrl)
            } else {
              resolve(existing.payload || null)
            }

            this.downloadPromises.get(globalUniqueIdentifier)?.delete(artifactId)
          })
          .catch((e: Error) => {
            this.downloadPromises.get(globalUniqueIdentifier)?.delete(artifactId)

            if (axios.isCancel(e)) {
              return resolve(null)
            }

            useLogsStore().addLogEntry({
              message: `Error downloading artifact ${artifactId} at height ${height}`,
              globalUniqueIdentifier,
              tag: 'artifact',
              error: e,
              level: 'ERROR',
              httpMethod: 'GET',
            })
            resolve(null)
          })
      })

      if (!this.downloadPromises.has(globalUniqueIdentifier)) {
        this.downloadPromises.set(globalUniqueIdentifier, new Map())
      }

      this.downloadPromises.get(globalUniqueIdentifier)!.set(artifactId, {
        promise: markRaw(promise),
        height: height === null ? undefined : height,
        controller: markRaw(controller),
      })

      return promise
    },

    /**
     * Fetches the Latest Artifact BEFORE a given timestamp (for a given artifact Type and Camera).
     * If `notOlderThan` is provided, it also ensures that the artifact is not older than `timestamp - notOlderThan`
     * Uses 'queryOneArtifact', so the resulting artifact is cached in 'this.artifacts'
     * @param cameraId
     * @param artifactType
     * @param timestamp
     * @param notOlderThan
     * @param globalUniqueIdentifier
     */
    async getLatestArtifactForCameraAndArtifactTypeWithTimestamp(
      cameraId: string,
      artifactType: string,
      timestamp: number,
      notOlderThan?: number,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<string | null> {
      const body: DocumentQueryPayload = {
        query: [
          { key: 'data.cameraId', operator: '==', value: cameraId },
          { key: 'type', operator: '==', value: artifactType },
          { key: 'timestamp', operator: '<=', value: timestamp },
          { key: 'status', operator: 'in', value: ['done', 'archived'] },
          { key: 'requestedBy.entityType', operator: '==', value: 'artifactJob' },
          ...((notOlderThan ? [{ key: 'timestamp', operator: '>=', value: timestamp - notOlderThan }] : []) as DocumentQuerySelect[]),
        ],
        order: { timestamp: 'desc' },
      }
      const maybeArtifact: Artifact | null = (await this.queryOneArtifact(body, globalUniqueIdentifier)) as Artifact | null
      if (!maybeArtifact) {
        return null
      }
      return maybeArtifact.artifactId
    },

    /**
     * Fetches the latest CanvasTimelapse (either mobile or not mobile), caches the record
     * @param mobile
     * @param globalUniqueIdentifier
     */
    async getLatestCanvasTimelapse(
      mobile: boolean = false,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId
    ): Promise<string | null> {
      const query: DocumentQueryPayload = {
        query: [
          { key: 'type', operator: '==', value: 'canvasTimelapse' },
          { key: 'status', operator: 'in', value: ['done', 'archived'] },
        ],
        order: { timestamp: 'desc' },
      }
      if (mobile) {
        ;(query.query as DocumentQuerySelect[]).push({
          key: 'tags',
          operator: 'contains',
          value: 'mobile',
        })
      } else {
        query.query = [
          [...(query.query as DocumentQuerySelect[]), { key: 'tags', operator: 'exists', value: false }],
          [...(query.query as DocumentQuerySelect[]), { key: 'tags', operator: 'not-contains', value: 'mobile' }],
        ]
      }
      const maybeArtifact = (await this.queryOneArtifact(query, globalUniqueIdentifier)) as Artifact | null
      if (!maybeArtifact) return null
      return maybeArtifact.artifactId
    },

    /**
     * Ensures an artifact is downloaded with the highest available quality, and then saves it to the file system.
     * Returns true if the process was successful, otherwise false.
     * @param artifactId
     * @param signal
     * @param globalUniqueIdentifier
     */
    async downloadArtifactPayloadAndSaveToFileSystem(
      artifactId: string,
      signal?: AbortSignal,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string
    ): Promise<boolean> {
      await this.loadArtifactData(artifactId, false, globalUniqueIdentifier)
      await this.loadArtifactPayload(artifactId, signal, null, false, globalUniqueIdentifier)

      if (!this.artifacts[globalUniqueIdentifier]?.[artifactId]?.downloaded) return false
      if (!this.artifacts[globalUniqueIdentifier][artifactId].data) return false
      const payload: string | null = this.artifacts[globalUniqueIdentifier][artifactId].payload
      if (!payload) return false
      useDownloadFile(payload, this.artifacts[globalUniqueIdentifier][artifactId].data.preferredFilename)
      return true
    },
  },
})
